import { logDebug } from '../utils/logger';
import { getCached, setCached, CACHE_TTL } from '../utils/cache';
import { getDeviceType } from '../utils/deviceDetection';

const GOOGLE_SCRIPT_URL = (process.env.REACT_APP_GOOGLE_SCRIPT_URL || '').trim();
const API_KEY = (process.env.REACT_APP_API_KEY || 'default-api-key-change-me').trim();

const appendApiKey = (params) => {
  if (API_KEY) {
    params.append('api_key', API_KEY);
  }
};

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

const fileToBase64WithRetry = async (file, maxRetries = 3) => {
  let lastError;
  for (let i = 0; i < maxRetries; i += 1) {
    try {
      return await fileToBase64(file);
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, 100 * Math.pow(2, i)));
      }
    }
  }
  throw lastError;
};

const generateRequestId = (type, employeeId) => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `${Date.now()}_${employeeId || 'emp'}_${type || 'attendance'}_${rand}`;
};

const collectDeviceMeta = () => {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return {};
    }

    const screenWidth = window.screen && window.screen.width ? window.screen.width : window.innerWidth || '';
    const screenHeight = window.screen && window.screen.height ? window.screen.height : window.innerHeight || '';
    const uaData = navigator.userAgentData || null;
    const uaDataMobile = uaData && typeof uaData.mobile === 'boolean' ? uaData.mobile : '';
    const uaDataPlatform = uaData && uaData.platform ? uaData.platform : '';

    return {
      device_type: getDeviceType(),
      user_agent: navigator.userAgent || '',
      platform: navigator.platform || '',
      webdriver: !!navigator.webdriver,
      ua_data_mobile: uaDataMobile,
      ua_data_platform: uaDataPlatform,
      max_touch_points: navigator.maxTouchPoints || 0,
      screen_width: screenWidth,
      screen_height: screenHeight,
      viewport_width: window.innerWidth || '',
      viewport_height: window.innerHeight || '',
    };
  } catch (error) {
    logDebug('[DeviceMeta] collect error:', error);
    return {};
  }
};

export const submitAttendance = async (data) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình. Vui lòng set REACT_APP_GOOGLE_SCRIPT_URL trong .env');
  }

  try {
    let imageBase64 = null;
    if (data.image) {
      imageBase64 = await fileToBase64WithRetry(data.image);
    }

    const params = new URLSearchParams();
    const deviceMeta = collectDeviceMeta();
    params.append('action', 'attendance');
    params.append('employee_id', data.employee_id ?? '');
    params.append('employee_code', data.employee_code ?? '');
    params.append('employee_name', data.employee_name ?? '');
    params.append('type', data.type ?? 'checkin');
    const requestId = (data.request_id || data.requestId || '').toString().trim() ||
      generateRequestId(data.type, data.employee_id);
    params.append('request_id', requestId);
    params.append('latitude', data.latitude ?? '');
    params.append('longitude', data.longitude ?? '');
    params.append('accuracy', data.accuracy ?? '');
    params.append('address', data.address ?? '');

    if (data.explanation) params.append('explanation', data.explanation);
    if (data.timestamp_ms !== undefined && data.timestamp_ms !== null && data.timestamp_ms !== '') {
      params.append('timestamp_ms', String(data.timestamp_ms));
    }
    if (data.force_manual_time !== undefined && data.force_manual_time !== null && data.force_manual_time !== '') {
      params.append('force_manual_time', String(data.force_manual_time));
    }
    if (data.project_code) params.append('project_code', data.project_code);
    if (data.project_name) params.append('project_name', data.project_name);
    if (data.vip !== undefined && data.vip !== null && data.vip !== '') {
      params.append('vip', String(data.vip));
    }
    if (imageBase64) params.append('image_base64', imageBase64);
    appendApiKey(params);
    if (deviceMeta.device_type) params.append('device_type', String(deviceMeta.device_type));
    if (deviceMeta.user_agent) params.append('user_agent', String(deviceMeta.user_agent));
    if (deviceMeta.platform) params.append('platform', String(deviceMeta.platform));
    if (deviceMeta.webdriver !== undefined) params.append('webdriver', String(deviceMeta.webdriver));
    if (deviceMeta.ua_data_mobile !== undefined && deviceMeta.ua_data_mobile !== '') params.append('ua_data_mobile', String(deviceMeta.ua_data_mobile));
    if (deviceMeta.ua_data_platform) params.append('ua_data_platform', String(deviceMeta.ua_data_platform));
    if (deviceMeta.max_touch_points !== undefined) params.append('max_touch_points', String(deviceMeta.max_touch_points));
    if (deviceMeta.screen_width !== undefined && deviceMeta.screen_width !== '') params.append('screen_width', String(deviceMeta.screen_width));
    if (deviceMeta.screen_height !== undefined && deviceMeta.screen_height !== '') params.append('screen_height', String(deviceMeta.screen_height));
    if (deviceMeta.viewport_width !== undefined && deviceMeta.viewport_width !== '') params.append('viewport_width', String(deviceMeta.viewport_width));
    if (deviceMeta.viewport_height !== undefined && deviceMeta.viewport_height !== '') params.append('viewport_height', String(deviceMeta.viewport_height));

    const response = await fetch(GOOGLE_SCRIPT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Response error:', response.status, errorText);
      throw new Error(`HTTP ${response.status}: ${errorText.substring(0, 100)}`);
    }

    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      return await response.json();
    }

    const text = await response.text();
    logDebug('Response text:', text);
    try {
      return JSON.parse(text);
    } catch {
      throw new Error('Server trả về dữ liệu không hợp lệ: ' + text.substring(0, 200));
    }
  } catch (error) {
    console.error('Error submitting attendance:', error);
    throw error;
  }
};

export const testGoogleScript = async () => {
  if (!GOOGLE_SCRIPT_URL) {
    return { ok: false, error: 'Google Script URL chưa được cấu hình' };
  }

  try {
    const response = await fetch(GOOGLE_SCRIPT_URL);
    const payload = await response.json();
    return { ok: true, data: payload };
  } catch (error) {
    return { ok: false, error: error.message };
  }
};

export const getEmployees = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!forceRefresh) {
    const cached = getCached('employees', CACHE_TTL.EMPLOYEES);
    if (cached) {
      logDebug('[Cache] Using cached employees');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'employees');
    appendApiKey(params);
    const response = await fetch(`${GOOGLE_SCRIPT_URL}?${params.toString()}`);
    const result = await response.json();
    if (result.ok && result.data) {
      setCached('employees', result.data);
      return result.data;
    }
    throw new Error(result.error || 'Không thể tải danh sách nhân viên');
  } catch (error) {
    console.error('Error loading employees:', error);
    throw error;
  }
};

export const getProjectLocations = async (forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  if (!forceRefresh) {
    const cached = getCached('projectLocations', CACHE_TTL.PROJECT_LOCATIONS);
    if (cached) {
      logDebug('[Cache] Using cached project locations');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'projectLocations');
    params.append('t', String(Date.now()));
    appendApiKey(params);
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
    const response = await fetch(url, { method: 'GET', cache: 'no-store' });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.ok && result.data) {
      setCached('projectLocations', result.data);
      return result.data;
    }

    console.warn('No project locations found or error:', result.error);
    return [];
  } catch (error) {
    console.error('Error loading project locations:', error);
    return [];
  }
};

const normalizeEmployeeIdentifier = (employeeInput, fallbackId = null) => {
  const fromStorage = (() => {
    try {
      const raw = localStorage.getItem('chamcong_employee');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  })();

  const readCode = (obj) =>
    String(
      (obj && (
        obj.code ||
        obj.employee_code ||
        obj.employeeCode ||
        obj.Code ||
        obj.CODE
      )) || ''
    ).trim();

  const readId = (obj) =>
    obj
      ? (obj.id ?? obj.employee_id ?? obj.employeeId ?? obj.ID ?? obj.Id ?? null)
      : null;

  if (employeeInput && typeof employeeInput === 'object') {
    const code = readCode(employeeInput) || readCode(fromStorage);
    const id = readId(employeeInput) ?? readId(fromStorage) ?? fallbackId ?? null;
    return { code, id };
  }

  const code = String(employeeInput || '').trim() || readCode(fromStorage);
  const id = readId(fromStorage) ?? fallbackId ?? null;
  return { code, id };
};

export const getTodayAttendance = async (employeeInput, fallbackId = null, forceRefresh = false) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  const { code: employeeCode, id: employeeId } = normalizeEmployeeIdentifier(employeeInput, fallbackId);
  if (!employeeCode && (employeeId === null || employeeId === undefined || employeeId === '')) {
    throw new Error('Missing employee identifier');
  }

  const cacheIdentity = employeeCode || String(employeeId);
  const cacheKey = `todayAttendance_${cacheIdentity}`;
  if (!forceRefresh) {
    const cached = getCached(cacheKey, CACHE_TTL.TODAY_ATTENDANCE);
    if (cached !== null) {
      logDebug('[Cache] Using cached today attendance');
      return cached;
    }
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'todayAttendance');
    appendApiKey(params);
    if (employeeCode) {
      params.append('employee_code', employeeCode);
    }
    if (employeeId !== null && employeeId !== undefined && employeeId !== '') {
      params.append('employee_id', String(employeeId));
    }
    params.append('t', String(Date.now()));
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;
    logDebug('Fetching today attendance from:', url);

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    logDebug('Today attendance response:', result);
    if (result.ok) {
      const payload = result.data;
      setCached(cacheKey, payload);
      return payload;
    }

    throw new Error(result.error || 'Không thể tải chấm công hôm nay');
  } catch (error) {
    console.error('Error loading today attendance:', error);
    return null;
  }
};

export const getAttendanceHistory = async (employeeInput, startDate = null, endDate = null, fallbackId = null) => {
  if (!GOOGLE_SCRIPT_URL) {
    throw new Error('Google Script URL chưa được cấu hình');
  }

  const { code: employeeCode, id: employeeId } = normalizeEmployeeIdentifier(employeeInput, fallbackId);
  if (!employeeCode && (employeeId === null || employeeId === undefined || employeeId === '')) {
    throw new Error('Missing employee identifier');
  }

  try {
    const params = new URLSearchParams();
    params.append('action', 'attendance');
    appendApiKey(params);
    if (employeeCode) {
      params.append('employee_code', employeeCode);
    }
    if (employeeId !== null && employeeId !== undefined && employeeId !== '') {
      params.append('employee_id', String(employeeId));
    }
    params.append('t', String(Date.now()));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const url = `${GOOGLE_SCRIPT_URL}?${params.toString()}`;

    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    if (result.ok) {
      return result.data || [];
    }

    console.error('API returned error:', result.error);
    return [];
  } catch (error) {
    console.error('Error loading attendance history:', error);
    return [];
  }
};
